import {
  IntegrationType,
  OrgProfileIntegration,
  TestCustomConnectionRequest,
  TestSFTPConnectionRequest,
} from '@/api/IntegrationType';
import aircallIcon from '@/assets/aircall.svg';
// import intercomIcon from '@/assets/intercom.svg';
import {
  CustomService,
  IntegrationsService,
  QuickbooksService,
  SftpService,
  SmtpServer,
  StripeService,
  WhatsAppService,
} from '@/api/';
import gmailIcon from '@/assets/gmail_icon.svg';
import outlookIcon from '@/assets/outlook.svg';
import quickbooksIcon from '@/assets/quickbooks.svg';
import stripeIcon from '@/assets/stripe.svg';
import metaIcon from '@/assets/meta.svg';
import zendeskIcon from '@/assets/zendesk.svg';
import DeleteSFTPIntegration from '@/components/DeleteSFTPIntegration';
import { NewIntegrationModal } from '@/components/NewIntegrationModal';
import { UpdateIntegrationModal } from '@/components/UpdateIntegrationModal';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { OAuthEmailIntegrationData } from '@/features/workflow/components/NodeConfigurations/EmailNotificationConfiguration';
import { useStore } from '@nanostores/react';
import { Link1Icon } from '@radix-ui/react-icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  BadgeCheck,
  DownloadCloudIcon,
  FolderIcon,
  HardDriveDownload,
  Loader2,
  PlusIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RevokeAccessTokenConfirmation } from './RevokeAccessTokenConfirmation';
import { RevokeApiKeyConfirmation } from './RevokeApiKeyConfirmation';
import { RevokeQuickbooksAccessTokenConfirmation } from './RevokeQuickbooksAccessTokenConfirmation';
import { RevokeStripeAccessTokenConfirmation } from './RevokeStripesAccessTokenConfirmation';
import { RevokeWhatsAppAccessTokenConfirmation } from './RevokeWhatsAppAccessTokenConfirmation';
import { TcnModal } from './TcnModal';
import { ZendeskSubDomainModal } from './ZendeskSubdomainModal';

type GmailIntegrationAccount = {
  smtpServer: SmtpServer;
  accountId: string;
  accountName: string;
  email: string;
  idToken: string;
  accessToken: string;
  refreshToken: string;
  scopes: Array<string>;
  grantType: string;
  authType: string;
  accessTokenExpiresAt: string;
};

export const Integrations = () => {
  const user = useStore($user);
  const profile = useStore($profile);
  const tcnApiKey = useStore($tcnApiKey);
  const org = useStore($org);
  const oauthEmailIntegrationService = new OauthEmailIntegrationService();

  const [showDrawer, setShowDrawer] = useState(false);
  const [isRevokeTokensModalOpen, setIsRevokeTokensModalOpen] = useState(false);
  const [isRevokeApiKeyModalOpen, setIsRevokeApiKeyModalOpen] = useState(false);
  const [
    isRevokeQuickbooksTokensModalOpen,
    setIsRevokeQuickbooksTokensModalOpen,
  ] = useState(false);
  const [updateIntegrationModalOpen, setUpdateIntegrationModalOpen] =
    useState(false); // Renamed variable for clarity
  const [isRevokeStripeTokensModalOpen, setIsRevokeStripeTokensModalOpen] =
    useState(false);
  const [isRevokeWhatsAppTokensModalOpen, setIsRevokeWhatsAppTokensModalOpen] =
    useState(false);

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [selectedIntegration, setSelectedIntegration] = useState<any>(null);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testedIntegrations, setTestedIntegrations] = useState<{
    [key: string]: boolean;
  }>({});
  const [testingIntegration, setTestingIntegration] = useState<string | null>(
    null,
  );
  const [googleOAuthCode, setGoogleOAuthCode] = useState<number | null>(null);
  const [loadingOAuthConnectionFlow, setLoadingOAuthConnectionFlow] =
    useState<boolean>(false);

  const [integrationToDelete, setIntegrationToDelete] = useState<string | null>(
    null,
  ); // Store the integration ID to delete
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const googleOAuthClientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;

  const [isZendeskSubdomainModalOpen, setIsZendeskSubdomainModalOpen] =
    useState(false);
  const [isTCNAPIKeyModalOpen, setIsTCNAPIKeyModalOpen] = useState(false);
  const [revokeTokenIntegrationType, setRevokeTokenIntegrationType] =
    useState<IntegrationType>('Zendesk');
  const [revokeTCNIntegrationType, setRevokeTCNIntegrationType] =
    useState<IntegrationType>('TCN');
  const [revokeQuickbooksIntegrationType, setRevokeQuickbooksIntegrationType] =
    useState<IntegrationType>('Quickbooks');
  const [revokeStripeIntegrationType, setRevokeStripeIntegrationType] =
    useState<IntegrationType>('Stripe');
  const [revokeWhatsAppIntegrationType, setRevokeWhatsAppIntegrationType] =
    useState<IntegrationType>('MetaWhatsApp');
  const [savedSmtpConnections, setSmtpConnections] = useState<
    OrgProfileIntegration[] | null
  >([]);

  const {
    data: activeIntegrations,
    isLoading: loadingSavedIntegrations,
    isFetching: fetchingSavedIntegrations,
    refetch: refetchSavedIntegrations,
  } = useQuery({
    queryKey: ['getIntegrations', org?.id],
    queryFn: async () => {
      if (!org) return;
      const res = await IntegrationsService.getOrgIntegrations(org?.id);


      // filter the email type integrations
      const emailIntegrations = res.filter(
        i =>
          i.integration?.type === 'Gmail' || i.integration?.type === 'Outlook',
      );
      // setting the email integrations to the state
      setSmtpConnections(emailIntegrations);
      return res;
    },
    refetchOnMount: true,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  async function handleConnectNewAccount(smtpServ: SmtpServer) {
    try {
      setLoadingOAuthConnectionFlow(true);
      let newOrgProfileIntegration;
      if (smtpServ === 'OUTLOOK') {
        newOrgProfileIntegration =
          await oauthEmailIntegrationService.connectOutlook(org!.id);
      }

      if (smtpServ === 'GMAIL') {
        newOrgProfileIntegration =
          await oauthEmailIntegrationService.connectGmail(org!.id);
      }

      if (!newOrgProfileIntegration) {
        setLoadingOAuthConnectionFlow(false);
        return;
      }

      // File specific implementation
      refetchSavedIntegrations();

      toast.success('Connected successfully!', {
        description: `Your ${newOrgProfileIntegration.integration?.type === 'Gmail' ? 'GMAIL' : 'OUTLOOK'} account has been connected.`,
        duration: 5000,
      });
    } catch (error) {
      toast.error('Failed to connect the account.');
    } finally {
      setLoadingOAuthConnectionFlow(false);
    }
  }

  function isOAuthEmailIntegrationData(
    data: any,
  ): data is OAuthEmailIntegrationData {
    return data.type === 'Outlook' || data.type === 'Gmail'; // Adjust with actual property checks
  }

  // Filter SFTP and Custom integrations
  const sftpAndCustomIntegrations = useMemo(() => {
    if (!activeIntegrations) return [];
    return activeIntegrations.filter(integration =>
      ['SFTP', 'Custom'].includes(integration?.integration?.type ?? ''),
    );
  }, [activeIntegrations]);

  console.log('sftpAndCustomIntegrations', sftpAndCustomIntegrations);

  // Use mutation for deleting the integration
  const deleteIntegrationMutation = useMutation({
    mutationFn: (id: string) => SftpService.deleteSftp(id),
    onSuccess: () => {
      // Refetch or update your active integrations
      refetchSavedIntegrations();
      setIsDeleteModalOpen(false);
      setIntegrationToDelete(null);
    },
    onError: error => {
      console.error('Error deleting integration:', error);
    },
  });
  const handleEditIntegration = (integration: any) => {
    setSelectedIntegration({
      id: integration.id,
      integrationType: integration.integration?.type,
      name: integration.name,
      sftpHost: integration.sftpHost,
      sftpUsername: integration.sftpUsername,
      sftpPassword: integration.sftpPassword,
      sftpPrivateKey: integration.sftpPrivateKey,
      sftpPublicKey: integration.sftpPublicKey,
      customHost: integration.customHost,
      customUsername: integration.customUsername,
      customPassword: integration.customPassword,
    });
    setTestedIntegrations(prev => {
      const updated = { ...prev };
      delete updated[integration.id]; // Remove the test status
      localStorage.setItem('testedIntegrations', JSON.stringify(updated)); // Persist the change
      return updated;
    });
    setUpdateIntegrationModalOpen(true); // Open the update modal
  };

  // Function to handle the delete action
  const handleDeleteIntegration = () => {
    if (integrationToDelete) {
      deleteIntegrationMutation.mutate(integrationToDelete);
    }
  };

  const testSFTPConnection = useMutation({
    mutationFn: async (payload: TestSFTPConnectionRequest) => {
      return SftpService.testSftpConnection(payload);
    },
    onSuccess: () => {
      // setTestResult('Connection successful!');
      toast.success('SFTP Connection tested successfully!');
    },
    onError: error => {
      setTestResult('Connection failed. Check your credentials.');
      toast.error('Failed to test SFTP connection.', {
        description: error.message,
      });
    },
  });

  const testCustomConnection = useMutation({
    mutationFn: async (payload: TestCustomConnectionRequest) => {
      return CustomService.testCustomConnection(payload);
    },
    onSuccess: () => {
      // setTestResult('Connection successful!');
      toast.success('Custom Connection tested successfully!');
    },
    onError: error => {
      setTestResult('Connection failed. Check your credentials.');
      toast.error('Failed to test Custom connection.', {
        description: error.message,
      });
    },
  });

  useEffect(() => {
    const savedState =
      JSON.parse(localStorage.getItem('testedIntegrations') || '{}') || {};
    setTestedIntegrations(savedState);
  }, []);
  const handleTestConnection = (integration: any) => {
    setTestingIntegration(integration.id); // Start tracking the test
    setTestResult(null); // Clear any previous results

    if (integration.integration.type === 'SFTP') {
      testSFTPConnection.mutate(
        {
          sftpHost: integration.sftpHost,
          sftpUsername: integration.sftpUsername,
          sftpPassword: integration.sftpPassword || '',
        },
        {
          onSuccess: response => {
            if (response.success) {
              console.log('SFTP connection successful for:', integration.name);
              setTestedIntegrations(prev => ({
                ...prev,
                [integration.id]: true,
              })); // Mark as tested
              setTestResult('Connection successful!');
              const updatedTested = {
                ...testedIntegrations,
                [integration.id]: true,
              };
              localStorage.setItem(
                'testedIntegrations',
                JSON.stringify(updatedTested),
              );
              toast.success(
                `SFTP connection for ${integration.name} is successful!`,
              );
            } else {
              console.error(
                'SFTP connection failed for:',
                integration.name,
                response.error,
              );
              setTestResult(`Connection failed: ${response.error}`);
              toast.error(`SFTP connection failed for ${integration.name}`);
            }
            setTestingIntegration(null);
          },
          onError: error => {
            setTestResult('Connection failed. Check your credentials.');
            toast.error(`SFTP connection failed for ${integration.name}`);
            setTestingIntegration(null);
          },
        },
      );
    } else if (integration.integration.type === 'Custom') {
      testCustomConnection.mutate(
        {
          customHost: integration.customHost,
          customUsername: integration.customUsername,
          customPassword: integration.customPassword || '',
        },
        {
          onSuccess: response => {
            if (response.success) {
              console.log(
                'Custom connection successful for:',
                integration.name,
              );
              setTestedIntegrations(prev => ({
                ...prev,
                [integration.id]: true,
              })); // Mark as tested
              setTestResult('Connection successful!');
              toast.success(
                `Custom connection for ${integration.name} is successful!`,
              );
              const updatedTested = {
                ...testedIntegrations,
                [integration.id]: true,
              };
              localStorage.setItem(
                'testedIntegrations',
                JSON.stringify(updatedTested),
              );
            } else {
              console.error(
                'Custom connection failed for:',
                integration.name,
                response.error,
              );
              setTestResult(`Connection failed: ${response.error}`);
              toast.error(`Custom connection failed for ${integration.name}`);
            }
            setTestingIntegration(null);
          },
          onError: error => {
            console.error(
              'Custom connection failed for:',
              integration.name,
              error,
            );
            setTestResult('Connection failed. Check your credentials.');
            toast.error(`Custom connection failed for ${integration.name}`);
            setTestingIntegration(null);
          },
        },
      );
    }
  };

  // zendesk oauth request
  const zendeskUrl = new URL(
    'https://d3v-tennisfinance.zendesk.com/oauth/authorizations/new',
  );
  zendeskUrl.searchParams.append('client_id', 'zdg-tennis-finance');
  zendeskUrl.searchParams.append(
    'redirect_uri',
    `${window.location.origin}/zendesk_oauth_redirect`,
  );
  zendeskUrl.searchParams.append('response_type', 'code');
  zendeskUrl.searchParams.append('state', `${Math.random() * 1000}`);
  zendeskUrl.searchParams.append('scope', 'read');

  return (
    <div>
      <div id="title">
        <p className="flex flex-1 items-center justify-start text-xl font-semibold tracking-tight">
          Integrations for {org?.name}
        </p>
        {/* <p className="text-sm text-muted-foreground">
          Add integrations to your organization to import data from other
          services.
        </p>
        <div className="flex items-center space-x-2">
          <Button onClick={() => setShowDrawer(true)}>
            <PlusIcon className="mr-2 h-4 w-4" /> Add New
          </Button>
        </div> */}
      </div>
      <div className="mt-6">
        <div className="grid items-stretch gap-4 sm:grid-cols-2">
          <Card className="">
            <CardHeader className="pb-3">
              <Avatar>
                <AvatarImage
                  src={stripeIcon}
                  className="dark:bg-muted-foreground"
                />
                <AvatarFallback>S</AvatarFallback>
              </Avatar>
              <CardTitle>Stripe</CardTitle>
              <CardDescription className="max-w-lg text-balance leading-relaxed">
                Integrate your Stripe account to import financial data and
                transactions.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const csrfToken = crypto.randomUUID();

                    // Store CSRF token
                    const response = await StripeService.storeStripeCsrfToken({
                      csrfToken: csrfToken,
                      organizationId: org?.id || '',
                    });

                    if (response.error) {
                      toast.error(response.error);
                      return;
                    }

                    // Proceed with OAuth flow
                    const stripeUrl = new URL(
                      `https://connect.stripe.com/oauth/authorize`,
                    );
                    const clientId = import.meta.env.VITE_STRIPE_CLIENT_ID;
                    stripeUrl.searchParams.append('client_id', clientId);
                    stripeUrl.searchParams.append('response_type', 'code');
                    stripeUrl.searchParams.append('scope', `read_write`);
                    stripeUrl.searchParams.append('state', csrfToken);
                    stripeUrl.searchParams.append(
                      'redirect_uri',
                      `${window.location.origin}/stripe_oauth_redirect`,
                    );

                    console.log(
                      'Successfully created stripe url',
                      stripeUrl.href,
                    );
                    window.location.href = stripeUrl.href;
                  } catch (error) {
                    console.error('Failed to initiate Stripe OAuth:', error);
                    toast.error('Failed to connect to  Stripe');
                  }
                }}
              >
                {loadingSavedIntegrations ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : activeIntegrations &&
                  activeIntegrations?.length > 0 &&
                  activeIntegrations?.find(
                    dat => dat?.integration?.type === 'Stripe',
                  ) !== undefined ? (
                  <BadgeCheck className="mr-2 h-4 w-4 text-green-600" />
                ) : (
                  <Link1Icon className="mr-2 h-4 w-4" />
                )}

                {loadingSavedIntegrations
                  ? 'Connect Stripe'
                  : activeIntegrations &&
                      activeIntegrations?.length > 0 &&
                      activeIntegrations?.find(
                        dat => dat?.integration?.type === 'Stripe',
                      )
                    ? 'Connected'
                    : 'Connect Stripe'}
              </Button>
              {activeIntegrations &&
                activeIntegrations?.length > 0 &&
                activeIntegrations?.find(
                  dat => dat?.integration?.type === 'Stripe',
                ) !== undefined && (
                  <Button
                    variant="destructive"
                    className="ml-2"
                    onClick={() => {
                      setRevokeStripeIntegrationType('Stripe');
                      setIsRevokeStripeTokensModalOpen(true);
                    }}
                  >
                    Revoke Access
                  </Button>
                )}
            </CardFooter>
          </Card>

          <Card className="">
            <CardHeader className="pb-3">
              <Avatar>
                <AvatarImage
                  src={quickbooksIcon}
                  className="dark:bg-muted-foreground"
                />
                <AvatarFallback>Q</AvatarFallback>
              </Avatar>
              <CardTitle>Quickbooks</CardTitle>
              <CardDescription className="max-w-lg text-balance leading-relaxed">
                Integrate your Quickbooks account to import financial data and
                transactions.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const csrfToken = crypto.randomUUID();

                    // Store CSRF token
                    const response =
                      await QuickbooksService.storeQuickbooksCsrfToken({
                        csrfToken: csrfToken,
                        organizationId: org?.id || '',
                        profileId: profile?.id || '',
                      });

                    if (response.error) {
                      toast.error(response.error);
                      return;
                    }

                    // Proceed with OAuth flow
                    const quickbooksUrl = new URL(
                      `https://appcenter.intuit.com/connect/oauth2`,
                    );
                    const clientId = import.meta.env.VITE_INTUIT_CLIENT_ID;
                    quickbooksUrl.searchParams.append('client_id', clientId);
                    quickbooksUrl.searchParams.append('response_type', 'code');
                    quickbooksUrl.searchParams.append(
                      'scope',
                      `com.intuit.quickbooks.accounting`,
                    );
                    quickbooksUrl.searchParams.append(
                      'redirect_uri',
                      `${window.location.origin}/quickbooks_oauth_redirect`,
                    );
                    quickbooksUrl.searchParams.append('state', csrfToken);

                    window.location.href = quickbooksUrl.href;
                  } catch (error) {
                    console.error(
                      'Failed to initiate Quickbooks OAuth:',
                      error,
                    );
                    toast.error('Failed to connect to Quickbooks');
                  }
                }}
              >
                {loadingSavedIntegrations ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : activeIntegrations &&
                  activeIntegrations?.length > 0 &&
                  activeIntegrations?.find(
                    dat => dat?.integration?.type === 'Quickbooks',
                  ) !== undefined ? (
                  <BadgeCheck className="mr-2 h-4 w-4 text-green-600" />
                ) : (
                  <Link1Icon className="mr-2 h-4 w-4" />
                )}

                {loadingSavedIntegrations
                  ? 'Connect Quickbooks'
                  : activeIntegrations &&
                      activeIntegrations?.length > 0 &&
                      activeIntegrations?.find(
                        dat => dat?.integration?.type === 'Quickbooks',
                      )
                    ? 'Connected'
                    : 'Connect Quickbooks'}
              </Button>
              {activeIntegrations &&
                activeIntegrations?.length > 0 &&
                activeIntegrations?.find(
                  dat => dat?.integration?.type === 'Quickbooks',
                ) !== undefined && (
                  <Button
                    variant="destructive"
                    className="ml-2"
                    onClick={() => {
                      setRevokeQuickbooksIntegrationType('Quickbooks');
                      setIsRevokeQuickbooksTokensModalOpen(true);
                    }}
                  >
                    Revoke Access
                  </Button>
                )}
            </CardFooter>
          </Card>

          <Card className="">
            <CardHeader className="pb-3">
              <Avatar>
                <AvatarImage
                  src={gmailIcon}
                  className="dark:bg-muted-foreground"
                />
                <AvatarFallback>G</AvatarFallback>
              </Avatar>
              <CardTitle>Gmail</CardTitle>
              <CardDescription className="max-w-lg text-balance leading-relaxed">
                Connect your Gmail account to send emails.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button
                variant="outline"
                onClick={() => handleConnectNewAccount('GMAIL')}
                disabled={loadingOAuthConnectionFlow}
              >
                {loadingOAuthConnectionFlow ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : savedSmtpConnections &&
                  savedSmtpConnections?.length > 0 &&
                  savedSmtpConnections?.find(
                    dat => dat?.integration?.type === 'Gmail',
                  ) !== undefined ? (
                  <BadgeCheck className="mr-2 h-4 w-4 text-green-600" />
                ) : (
                  <Link1Icon className="mr-2 h-4 w-4" />
                )}
                {loadingOAuthConnectionFlow
                  ? 'Connecting...'
                  : savedSmtpConnections &&
                    savedSmtpConnections?.length > 0 &&
                    savedSmtpConnections?.find(
                      dat => dat?.integration?.type === 'Gmail',
                    )
                    ? 'Connected'
                    : 'Connect Gmail'}
              </Button>
              {savedSmtpConnections &&
                savedSmtpConnections?.length > 0 &&
                savedSmtpConnections?.find(
                  dat => dat?.integration?.type === 'Gmail',
                ) !== undefined && (
                  <Button
                    variant="destructive"
                    className="ml-2"
                    onClick={() => {
                      setRevokeTokenIntegrationType('Gmail');
                      setIsRevokeTokensModalOpen(true);
                    }}
                  >
                    Revoke Access
                  </Button>
                )}
            </CardFooter>
          </Card>

          <Card className="">
            <CardHeader className="pb-3">
              <Avatar>
                <AvatarImage
                  src={outlookIcon}
                  className="dark:bg-muted-foreground"
                />
                <AvatarFallback>O</AvatarFallback>
              </Avatar>
              <CardTitle>Outlook</CardTitle>
              <CardDescription className="max-w-lg text-balance leading-relaxed">
                Connect your Outlook account to send emails.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button
                variant="outline"
                onClick={() => handleConnectNewAccount('OUTLOOK')}
                disabled={loadingOAuthConnectionFlow}
              >
                {loadingOAuthConnectionFlow ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : savedSmtpConnections &&
                  savedSmtpConnections?.length > 0 &&
                  savedSmtpConnections?.find(
                    dat => dat?.integration?.type === 'Outlook',
                  ) !== undefined ? (
                  <BadgeCheck className="mr-2 h-4 w-4 text-green-600" />
                ) : (
                  <Link1Icon className="mr-2 h-4 w-4" />
                )}
                {loadingOAuthConnectionFlow
                  ? 'Connecting...'
                  : savedSmtpConnections &&
                    savedSmtpConnections?.length > 0 &&
                    savedSmtpConnections?.find(
                      dat => dat?.integration?.type === 'Outlook',
                    )
                    ? 'Connected'
                    : 'Connect Outlook'}
              </Button>
              {savedSmtpConnections &&
                savedSmtpConnections?.length > 0 &&
                savedSmtpConnections?.find(
                  dat => dat?.integration?.type === 'Outlook',
                ) !== undefined && (
                  <Button
                    variant="destructive"
                    className="ml-2"
                    onClick={() => {
                      setRevokeTokenIntegrationType('Outlook');
                      setIsRevokeTokensModalOpen(true);
                    }}
                  >
                    Revoke Access
                  </Button>
                )}
            </CardFooter>
          </Card>

          <Card className="">
            <CardHeader className="pb-3">
              <Avatar className="">
                <AvatarImage
                  src={zendeskIcon}
                  className="dark:bg-muted-foreground"
                />
                <AvatarFallback>Z</AvatarFallback>
              </Avatar>
              <CardTitle>Zendesk</CardTitle>
              <CardDescription className="max-w-lg text-balance leading-relaxed">
                Integrate your Zendesk account to import tickets and customer
                data.
              </CardDescription>
            </CardHeader>
            <CardFooter className="">
              <Button
                disabled={
                  loadingSavedIntegrations ||
                  (activeIntegrations &&
                    activeIntegrations?.length > 0 &&
                    activeIntegrations?.find(
                      dat => dat?.integration?.type === 'Zendesk',
                    ) !== undefined)
                }
                variant="outline"
                onClick={() => {
                  setIsZendeskSubdomainModalOpen(true);
                }}
              >
                {loadingSavedIntegrations ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : activeIntegrations &&
                  activeIntegrations?.length > 0 &&
                  activeIntegrations?.find(
                    dat => dat?.integration?.type === 'Zendesk',
                  ) !== undefined ? (
                  <BadgeCheck className="mr-2 h-4 w-4 text-green-600" />
                ) : (
                  <Link1Icon className="mr-2 h-4 w-4" />
                )}

                {loadingSavedIntegrations
                  ? 'Connect Zendesk'
                  : activeIntegrations &&
                      activeIntegrations?.length > 0 &&
                      activeIntegrations?.find(
                        dat => dat?.integration?.type === 'Zendesk',
                      )
                    ? 'Connected'
                    : 'Connect Zendesk'}
              </Button>

              {activeIntegrations &&
                activeIntegrations?.length > 0 &&
                activeIntegrations?.find(
                  dat => dat?.integration?.type === 'Zendesk',
                ) !== undefined && (
                  <Button
                    variant="destructive"
                    className="ml-2"
                    onClick={() => {
                      setRevokeTokenIntegrationType('Zendesk');
                      setIsRevokeTokensModalOpen(true);
                    }}
                  >
                    Revoke Access
                  </Button>
                )}
            </CardFooter>
          </Card>

          <Card className="">
            <CardHeader className="pb-3">
              <Avatar>
                <AvatarImage
                  src={aircallIcon}
                  className="dark:bg-muted-foreground"
                />
                <AvatarFallback>A</AvatarFallback>
              </Avatar>
              <CardTitle>Aircall</CardTitle>
              <CardDescription className="max-w-lg text-balance leading-relaxed">
                Integrate your Aircall account to import call data and customer
                information.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button
                variant="outline"
                onClick={() => {
                  const aircallUrl = new URL(
                    `https://dashboard.aircall.io/oauth/authorize`,
                  );
                  aircallUrl.searchParams.append(
                    'client_id',
                    'B5N45-v3bd3C-oB2vLjK4rJqgaJZF3heIdntCJ-MBPQ',
                  );
                  aircallUrl.searchParams.append(
                    'redirect_uri',
                    `${window.location.origin}/aircall_oauth_redirect`,
                  );
                  aircallUrl.searchParams.append('response_type', 'code');
                  aircallUrl.searchParams.append('scope', `public_api`);
                  zendeskUrl.searchParams.append('state', `rally`);
                  // zendeskUrl.searchParams.append('subdomain', subdomain);
                  window.location.href = aircallUrl.href;
                }}
              >
                {loadingSavedIntegrations ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : activeIntegrations &&
                  activeIntegrations?.length > 0 &&
                  activeIntegrations?.find(
                    dat => dat?.integration?.type === 'Aircall',
                  ) !== undefined ? (
                  <BadgeCheck className="mr-2 h-4 w-4 text-green-600" />
                ) : (
                  <Link1Icon className="mr-2 h-4 w-4" />
                )}

                {loadingSavedIntegrations
                  ? 'Connect Aircall'
                  : activeIntegrations &&
                      activeIntegrations?.length > 0 &&
                      activeIntegrations?.find(
                        dat => dat?.integration?.type === 'Aircall',
                      )
                    ? 'Connected'
                    : 'Connect Aircall'}
              </Button>
            </CardFooter>
          </Card>

          <Card className="">
            <CardHeader className="pb-3">
              <Avatar>
                <AvatarImage
                  src={metaIcon}
                  className="dark:bg-muted-foreground"
                />
                <AvatarFallback>f</AvatarFallback>
              </Avatar>
              <CardTitle>WhatsApp</CardTitle>
              <CardDescription className="max-w-lg text-balance leading-relaxed">
                Integrate your WhatsApp account to send and receive messages.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const csrfToken = crypto.randomUUID();

                    // Store CSRF token
                    const response = await WhatsAppService.storeWhatsAppCsrfToken({
                      csrfToken: csrfToken,
                      organizationId: org?.id || ''
                    });

                    if (response.error) {
                      toast.error(response.error);
                      return;
                    }

                    // Proceed with OAuth flow
                    const whatsappUrl = new URL(
                      `https://www.facebook.com/v21.0/dialog/oauth`,
                    );
                    const clientId = import.meta.env.VITE_META_WHATSAPP_CLIENT_ID;
                    whatsappUrl.searchParams.append('client_id', clientId);
                    whatsappUrl.searchParams.append('response_type', 'code');
                    whatsappUrl.searchParams.append('scope', `whatsapp_business_manage_events, whatsapp_business_messaging`);
                    whatsappUrl.searchParams.append('state', csrfToken);
                    whatsappUrl.searchParams.append(
                      'redirect_uri',
                      `${window.location.origin}/whatsapp_oauth_redirect`,
                    );

                    console.log(
                      'Successfully created whatsapp url',
                      whatsappUrl.href,
                    );
                    window.location.href = whatsappUrl.href;
                  } catch (error) {
                    console.error('Failed to initiate WhatsApp OAuth:', error);
                    toast.error('Failed to connect to WhatsApp');
                  }
                }}
              >
                {loadingSavedIntegrations ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : activeIntegrations &&
                  activeIntegrations?.length > 0 &&
                  activeIntegrations?.find(
                    dat => dat?.integration?.type === 'MetaWhatsApp',
                  ) !== undefined ? (
                  <BadgeCheck className="mr-2 h-4 w-4 text-green-600" />
                ) : (
                  <Link1Icon className="mr-2 h-4 w-4" />
                )}

                {loadingSavedIntegrations
                  ? 'Connect WhatsApp'
                  : activeIntegrations &&
                      activeIntegrations?.length > 0 &&
                      activeIntegrations?.find(
                        dat => dat?.integration?.type === 'MetaWhatsApp',
                      )
                    ? 'Connected'
                    : 'Connect WhatsApp'}
              </Button>
              {activeIntegrations &&
                activeIntegrations?.length > 0 &&
                activeIntegrations?.find(
                  dat => dat?.integration?.type === 'MetaWhatsApp',
                ) !== undefined && (
                  <Button
                    variant="destructive"
                    className="ml-2"
                    onClick={() => {
                      setRevokeWhatsAppIntegrationType('MetaWhatsApp');
                      setIsRevokeWhatsAppTokensModalOpen(true);
                    }}
                  >
                    Revoke Access
                  </Button>
                )}
            </CardFooter>
          </Card>

          <Card className="">
            <CardHeader className="pb-3">
              <Avatar className="p-2 ">
                <DownloadCloudIcon className="h-6 w-6 " />
              </Avatar>
              <CardTitle>Add New Integration</CardTitle>
              <CardDescription className="max-w-lg text-balance leading-relaxed">
                Add an SFTP or Custom integration to import or export data.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedIntegration(null); // Reset for new integration
                  setShowDrawer(true);
                }}
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                Add Integration
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteSFTPIntegration
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDeleteIntegration} // Use the handleDeleteIntegration function
      />
      <RevokeAccessTokenConfirmation
        isOpen={isRevokeTokensModalOpen}
        handleOpen={setIsRevokeTokensModalOpen}
        integrationType={revokeTokenIntegrationType}
      />
      <RevokeQuickbooksAccessTokenConfirmation
        isOpen={isRevokeQuickbooksTokensModalOpen}
        handleOpen={setIsRevokeQuickbooksTokensModalOpen}
        integrationType={revokeQuickbooksIntegrationType}
      />
      <RevokeStripeAccessTokenConfirmation
        isOpen={isRevokeStripeTokensModalOpen}
        handleOpen={setIsRevokeStripeTokensModalOpen}
        integrationType={revokeStripeIntegrationType}
      />
      <RevokeWhatsAppAccessTokenConfirmation
        isOpen={isRevokeWhatsAppTokensModalOpen}
        handleOpen={setIsRevokeWhatsAppTokensModalOpen}
        integrationType={revokeWhatsAppIntegrationType}
      />
      <ZendeskSubDomainModal
        open={isZendeskSubdomainModalOpen}
        setOpen={setIsZendeskSubdomainModalOpen}
      />
      <RevokeApiKeyConfirmation
        isOpen={isRevokeApiKeyModalOpen}
        handleOpen={setIsRevokeApiKeyModalOpen}
        integrationType={revokeTCNIntegrationType}
      />
      <TcnModal open={isTCNAPIKeyModalOpen} setOpen={setIsTCNAPIKeyModalOpen} />
      <NewIntegrationModal
        open={showDrawer}
        setOpen={setShowDrawer}
        organizationId={org?.id || ''}
      />
      {updateIntegrationModalOpen && (
        <UpdateIntegrationModal
          open={updateIntegrationModalOpen}
          setOpen={setUpdateIntegrationModalOpen}
          orgId={org?.id || ''}
          editingIntegration={selectedIntegration}
        />
      )}
    </div>
  );
};
